import { useCallback, useState } from "react";

/**
 * "Select one item, show a confirmation prompt for it, then either confirm (run the action and
 * clear the pending item) or cancel (just clear it)" — the flow behind ChatHistoryList's
 * delete-conversation confirmation modal. Kept generic over `T` so any other select-then-confirm
 * flow (delete/discard/overwrite, etc.) can reuse it instead of hand-rolling its own
 * `useState<T | null>` plus two `useCallback`s for cancel/confirm.
 *
 * `confirm` reads the pending item via the state updater form (`setPending(current => ...)`)
 * instead of closing over `pending` directly, so its identity only changes when `onConfirm`
 * itself does — the request/cancel/confirm callbacks stay referentially stable across
 * re-renders caused by picking a different pending item.
 */
export function usePendingConfirmation<T>(
  onConfirm: (item: T) => void
): {
  pending: T | null;
  request: (item: T) => void;
  cancel: () => void;
  confirm: () => void;
} {
  const [pending, setPending] = useState<T | null>(null);

  const request = useCallback((item: T) => setPending(item), []);
  const cancel = useCallback(() => setPending(null), []);
  const confirm = useCallback(() => {
    setPending(current => {
      if (current !== null) onConfirm(current);
      return null;
    });
  }, [onConfirm]);

  return { pending, request, cancel, confirm };
}
